/**
 * A origem do caso aberto pela operação (E177.F1, GUS-253 A1, ADR-0131). Espelha
 * `apps/api/types/origem_do_caso.py`.
 *
 * - `GET /api/origens-do-caso`: os bancos cadastrados, cada um com os escritórios vinculados — o
 *   escritório requerente da abertura (entre os clientes) e a escolha da mesa.
 * - `PUT /api/casos/{id}/origem`: a mesa informa o banco do caso que nasceu sem ele; vale para o
 *   processo inteiro. Só preenche: com banco já gravado, 409 `BANCO_JA_INFORMADO`.
 */
import axios, { type AxiosInstance } from 'axios';

export interface EscritorioDaOrigemData {
  id: string;
  nome: string;
}

export interface BancoDaOrigemData {
  id: string;
  nome: string;
  escritorios: EscritorioDaOrigemData[];
}

/** O que a mesa escolhe: o banco (obrigatório) e o escritório requerente vinculado a ele. */
export interface OrigemEscolhida {
  banco: string | null;
  escritorio: string | null;
}

export const ORIGEM_VAZIA: OrigemEscolhida = { banco: null, escritorio: null };

export interface OrigemGravadaData {
  banco_id: string;
  escritorio_id: string | null;
  casos: string[];
}

export type OrigemErrorReason = 'banco_ja_informado' | 'origem_invalida' | 'unknown';

export class OrigemError extends Error {
  public readonly reason: OrigemErrorReason;

  constructor(reason: OrigemErrorReason) {
    super(reason);
    this.reason = reason;
    this.name = 'OrigemError';
  }
}

export async function listarOrigens(client: AxiosInstance): Promise<BancoDaOrigemData[]> {
  const { data } = await client.get<BancoDaOrigemData[]>('/origens-do-caso');
  return data;
}

/** Um escritório cliente com os bancos que ele atende — a opção da abertura pela operação. */
export interface EscritorioClienteData {
  id: string;
  nome: string;
  bancos: string[];
}

/** Os escritórios clientes, um por escritório, com os bancos que ele atende, em ordem de nome. */
export function escritoriosClientes(bancos: readonly BancoDaOrigemData[]): EscritorioClienteData[] {
  const porId = new Map<string, EscritorioClienteData>();
  for (const banco of bancos) {
    for (const e of banco.escritorios) {
      const atual = porId.get(e.id) ?? { id: e.id, nome: e.nome, bancos: [] };
      atual.bancos.push(banco.nome);
      porId.set(e.id, atual);
    }
  }
  return [...porId.values()].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
}

/** Os escritórios vinculados ao banco escolhido; nenhum sem banco. */
export function escritoriosDoBanco(
  bancos: readonly BancoDaOrigemData[],
  bancoId: string | null,
): EscritorioDaOrigemData[] {
  return bancos.find((b) => b.id === bancoId)?.escritorios ?? [];
}

function motivo(err: unknown): OrigemErrorReason {
  if (!axios.isAxiosError(err)) return 'unknown';
  const detalhe = (err.response?.data as { detail?: unknown } | undefined)?.detail;
  if (err.response?.status === 409 && detalhe === 'BANCO_JA_INFORMADO') return 'banco_ja_informado';
  if (err.response?.status === 422 && detalhe === 'ORIGEM_INVALIDA') return 'origem_invalida';
  return 'unknown';
}

export async function informarOrigem(
  client: AxiosInstance,
  caseId: string,
  origem: { banco: string; escritorio: string | null },
): Promise<OrigemGravadaData> {
  try {
    const { data } = await client.put<OrigemGravadaData>(`/casos/${caseId}/origem`, {
      banco_id: origem.banco,
      escritorio_id: origem.escritorio,
    });
    return data;
  } catch (err) {
    throw new OrigemError(motivo(err));
  }
}
